import { useEffect, useRef, useState, useCallback } from 'react';
import { getWsToken } from '@/api/agents';

export interface ToolCall {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'running' | 'completed' | 'error';
  usage?: {
    inputTokens: number;
    outputTokens: number;
    cumulativeInputTokens: number;
    cumulativeOutputTokens: number;
  };
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  inputCost: number;
  outputCost: number;
  totalCost: number;
}

export interface AssistantTurn {
  id: string;
  thinking?: string;
  toolCalls: ToolCall[];
  response?: string;
  isLoading: boolean;
  timestamp: Date;
  usage?: TokenUsage;
}

export interface ImageAttachment {
  type: 'image';
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
  data: string; // base64 encoded (without data: prefix)
  previewUrl?: string; // For displaying in the UI
}

export interface TextFileAttachment {
  type: 'text_file';
  filename: string;
  mimeType: string; // text/csv, text/plain, application/json, etc.
  content: string; // raw text content
}

export type FileAttachment = ImageAttachment | TextFileAttachment;

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  images?: ImageAttachment[]; // Images attached to this message
  textFiles?: TextFileAttachment[]; // Text files attached to this message
  timestamp: Date;
  turn?: AssistantTurn;
}

interface UseOdooAgentOptions {
  agentUrl?: string;
  odooUserId?: number;
  odooUserName?: string;
}

export interface OdooModelPreview {
  model: string;
  table: string;
  name: string;
  field_count: number;
  sample_fields: string[];
  relations: Array<{ field: string; target: string }>;
}

export interface OdooFieldInfo {
  type: string;
  label: string | Record<string, string>;
  required?: boolean;
  relation?: string;
  relation_table?: string;
}

export interface OdooModelFull {
  table: string;
  name: string | Record<string, string>;
  fields: Record<string, OdooFieldInfo>;
}

export interface OdooSchema {
  model_count: number;
  table_count: number;
  models_preview: OdooModelPreview[];
  full_schema?: Record<string, OdooModelFull>;
  // Debug info for comparing formats
  prompt_format?: string;
  prompt_tokens?: number;
  json_tokens?: number;
}

export function useOdooAgent(
  projectId: string,
  buildId: number,
  fqdn?: string | null,
  options: UseOdooAgentOptions = {}
) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schemaStatus, setSchemaStatus] = useState<'loading' | 'loaded' | 'error' | null>(null);
  const [schema, setSchema] = useState<OdooSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const pendingConversationIdRef = useRef<string | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const currentTurnIdRef = useRef<string | null>(null);

  const agentUrl = options.agentUrl || import.meta.env.VITE_AGENT_URL || '';

  const connect = useCallback(async () => {
    if (!agentUrl || !projectId || !buildId) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Fetch auth token before connecting
      let wsUrl = `${agentUrl}/agent/${projectId}/${buildId}`;
      try {
        const { token } = await getWsToken(projectId, buildId);
        wsUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;
      } catch (tokenError) {
        console.warn('Failed to get WS token, trying without auth:', tokenError);
      }

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        reconnectAttemptsRef.current = 0;

        ws.send(JSON.stringify({
          type: 'init',
          projectId,
          buildId,
          sessionId: crypto.randomUUID(),
          fqdn: fqdn || undefined,
          odooUserId: options.odooUserId || undefined,
          odooUserName: options.odooUserName || undefined,
        }));
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);
        wsRef.current = null;

        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = () => {
        setError('Connection error');
        setIsConnecting(false);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'thinking':
              if (data.content?.includes('Loading Odoo schema')) {
                setSchemaStatus('loading');
              } else if (data.content?.includes('SUCCESS: Loaded')) {
                setSchemaStatus('loaded');
              }
              setMessages(prev => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                  lastMsg.turn.thinking = data.content;
                }
                return updated;
              });
              break;

            case 'tool_start':
              setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                  const alreadyExists = lastMsg.turn.toolCalls.some(
                    tc => tc.name === data.tool && tc.status === 'running'
                  );
                  if (alreadyExists) return prev;
                  
                  const toolCall: ToolCall = {
                    id: crypto.randomUUID(),
                    name: data.tool,
                    args: data.toolArgs,
                    status: 'running',
                  };
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMsg,
                      turn: {
                        ...lastMsg.turn,
                        toolCalls: [...lastMsg.turn.toolCalls, toolCall],
                      },
                    },
                  ];
                }
                return prev;
              });
              break;

            case 'tool_result':
              if (data.tool === 'schema_loaded') {
                setSchemaStatus('loaded');
                setSchema(data.result as OdooSchema);
                setSchemaError(null);
                break;
              }
              if (data.tool === 'schema_load_error') {
                setSchemaStatus('error');
                setSchemaError(JSON.stringify(data.result, null, 2));
                break;
              }
              setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                  const toolCallIndex = lastMsg.turn.toolCalls.findIndex(
                    tc => tc.name === data.tool && tc.status === 'running'
                  );
                  if (toolCallIndex === -1) return prev;

                  const updatedToolCalls = lastMsg.turn.toolCalls.map((tc, i) =>
                    i === toolCallIndex
                      ? {
                          ...tc,
                          result: data.result,
                          status: ((data.result as { error?: unknown })?.error ? 'error' : 'completed') as ToolCall['status'],
                          // Include per-tool-call usage from backend
                          usage: data.usage ? {
                            inputTokens: data.usage.inputTokens,
                            outputTokens: data.usage.outputTokens,
                            cumulativeInputTokens: data.usage.cumulativeInputTokens,
                            cumulativeOutputTokens: data.usage.cumulativeOutputTokens,
                          } : undefined,
                        }
                      : tc
                  );
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMsg,
                      turn: {
                        ...lastMsg.turn,
                        toolCalls: updatedToolCalls,
                      },
                    },
                  ];
                }
                return prev;
              });
              break;

            case 'message_chunk':
              setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                  const newContent = (lastMsg.content || '') + data.content;
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMsg,
                      content: newContent,
                      turn: {
                        ...lastMsg.turn,
                        response: newContent,
                      },
                    },
                  ];
                }
                return prev;
              });
              break;

            case 'message_done':
              // Use queueMicrotask to ensure the message update has been processed first
              queueMicrotask(() => {
                setMessages(prev => {
                  const lastMsg = prev[prev.length - 1];
                  if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                    return [
                      ...prev.slice(0, -1),
                      {
                        ...lastMsg,
                        turn: {
                          ...lastMsg.turn,
                          isLoading: false,
                          usage: data.usage as TokenUsage | undefined,
                        },
                      },
                    ];
                  }
                  return prev;
                });
              });
              currentTurnIdRef.current = null;
              setIsLoading(false);
              break;

            case 'message':
              setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                // If there's an assistant message with a turn, update it (preserving the turn for usage)
                if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMsg,
                      content: data.content,
                      turn: {
                        ...lastMsg.turn,
                        response: data.content,
                        isLoading: false,
                      },
                    },
                  ];
                }
                // Fallback: create new message (shouldn't normally happen in our flow)
                return [
                  ...prev,
                  {
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: data.content,
                    timestamp: new Date(),
                  },
                ];
              });
              currentTurnIdRef.current = null;
              setIsLoading(false);
              break;

            case 'error':
              setMessages(prev => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                
                if (lastMsg?.role === 'assistant' && lastMsg.turn?.isLoading) {
                  lastMsg.content = `Error: ${data.error}`;
                  lastMsg.turn.response = `Error: ${data.error}`;
                  lastMsg.turn.isLoading = false;
                  currentTurnIdRef.current = null;
                  return updated;
                }
                
                return [
                  ...prev,
                  {
                    id: crypto.randomUUID(),
                    role: 'assistant',
                    content: `Error: ${data.error}`,
                    timestamp: new Date(),
                  },
                ];
              });
              setIsLoading(false);
              break;

            case 'usage_update':
              // Real-time usage update after each Claude API call
              setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.role === 'assistant' && lastMsg.turn) {
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMsg,
                      turn: {
                        ...lastMsg.turn,
                        usage: {
                          inputTokens: data.usage.inputTokens,
                          outputTokens: data.usage.outputTokens,
                          totalTokens: data.usage.totalTokens,
                          inputCost: data.usage.inputTokens * 0.001 / 1000,
                          outputCost: data.usage.outputTokens * 0.005 / 1000,
                          totalCost: data.usage.estimatedCost,
                        },
                      },
                    },
                  ];
                }
                return prev;
              });
              break;

            case 'pong':
              break;

            case 'ready':
              if (data.conversationId) {
                setConversationId(data.conversationId);
              }
              if (pendingConversationIdRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ 
                  type: 'resume_conversation', 
                  conversationId: pendingConversationIdRef.current 
                }));
              }
              break;

            case 'conversation_resumed':
              if (data.conversationId) {
                setConversationId(data.conversationId);
                pendingConversationIdRef.current = null;
              }
              break;

            case 'conversation_created':
              if (data.conversationId) {
                setConversationId(data.conversationId);
              }
              break;
          }
        } catch (e) {
          console.error('Failed to parse message:', e);
        }
      };

      wsRef.current = ws;
    } catch (e) {
      setError('Failed to connect');
      setIsConnecting(false);
    }
  }, [agentUrl, projectId, buildId, fqdn, options.odooUserId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    disconnect();
    setTimeout(() => connect(), 100);
  }, [disconnect, connect]);

  const sendMessage = useCallback((content: string, images?: ImageAttachment[], textFiles?: TextFileAttachment[]) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('Not connected');
      return;
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      images, // Include images in the message
      textFiles, // Include text files in the message
      timestamp: new Date(),
    };

    const turnId = crypto.randomUUID();
    currentTurnIdRef.current = turnId;

    const assistantMessage: Message = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      turn: {
        id: turnId,
        toolCalls: [],
        isLoading: true,
        timestamp: new Date(),
      },
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setIsLoading(true);

    // Prepare images for WebSocket (strip previewUrl which is only for UI)
    const wsImages = images?.map(img => ({
      type: img.type,
      mediaType: img.mediaType,
      data: img.data,
    }));

    // Text files are sent as-is (no transformation needed)
    const wsTextFiles = textFiles?.map(file => ({
      type: file.type,
      filename: file.filename,
      mimeType: file.mimeType,
      content: file.content,
    }));

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content,
      images: wsImages,
      textFiles: wsTextFiles,
      conversationId: pendingConversationIdRef.current || undefined,
    }));
  }, []);

  const refineQuery = useCallback((
    baseQuery: { primaryTable: string; joins?: string; conditions?: string; orderBy?: string; limit?: number },
    selectedColumns: string[]
  ) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('Not connected');
      return;
    }

    const turnId = crypto.randomUUID();
    currentTurnIdRef.current = turnId;

    const assistantMessage: Message = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      turn: {
        id: turnId,
        toolCalls: [],
        isLoading: true,
        timestamp: new Date(),
      },
    };

    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(true);

    wsRef.current.send(JSON.stringify({ 
      type: 'refine_query', 
      baseQuery, 
      selectedColumns 
    }));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    currentTurnIdRef.current = null;
    setConversationId(null);
    pendingConversationIdRef.current = null;
  }, []);

  const loadMessages = useCallback((loadedMessages: Message[], existingConversationId?: string) => {
    setMessages(loadedMessages);
    currentTurnIdRef.current = null;
    if (existingConversationId) {
      setConversationId(existingConversationId);
      pendingConversationIdRef.current = existingConversationId;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ 
          type: 'resume_conversation', 
          conversationId: existingConversationId 
        }));
      }
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  return {
    messages,
    sendMessage,
    refineQuery,
    clearMessages,
    loadMessages,
    isConnected,
    isConnecting,
    isLoading,
    error,
    connect,
    disconnect,
    reconnect,
    schema,
    schemaStatus,
    schemaError,
    conversationId,
  };
}
